import _ from 'lodash';
import { recordException } from './AnalyticsService';
import StorageService from './StorageService';
import GeocoderService from './GeocoderService';
import { ADDRESS_CATEGORIES, POI_CATEGORY_ICON, UNTYPED_CATEGORY_ICON } from '../constants/addressCategories';

const MAX_RECENT_STORAGE_SIZE = 10;
export const RECENT_ADDRESS_LIST_STORAGE_KEYS = {
  LIVE_DEPARTURES: 'RECENT_ADDRESS_LIST_LIVE_DEPARTURES',
  JOURNEY_PLANNER: 'RECENT_ADDRESS_LIST_JOURNEY_PLANNER',
  TIMETABLES: 'RECENT_ADDRESS_LIST_TIMETABLES',
};

const recentSearchSectionTitle = 'Recent searches / Rapunga tata';
const TOP_SUGGESTIONS = {
  CURRENT_LOCATION: { address: 'Current location / Wāhi ināianei', category: ADDRESS_CATEGORIES.GPS },
  FIND_ON_MAP: { address: 'Find on map / Kimikimi wāhi', category: ADDRESS_CATEGORIES.MAPSTOP },
};

export function getAddressLabel(address) {
  return address?.displayText ?? address?.address;
}

export function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, { maximumAge: 30000 });
  });
}

export async function searchAddresses(query) {
  // Feature detection for navigator.permissions - this has not been implemented in safari until version 16. In the
  // cases where the feature is not present or the feature does not properly return "granted", proximity geocoding
  // is disabled.
  let currentPosition;
  if ('permissions' in navigator
    && (await navigator.permissions.query({ name: 'geolocation' })).state === 'granted'
  ) {
    try {
      currentPosition = await getCurrentPosition();
    } catch (error) {
      recordException({ description: `Failed to get the user's location: ${error.code} - ${error.message}` });
    }
  }
  return GeocoderService.getInstance().forwardGeocode(query, currentPosition?.coords);
}

export function getAddressFromPosition({ latitude, longitude }) {
  return GeocoderService.getInstance().reverseGeocode(latitude, longitude)
    .catch(() => Promise.reject(new Error('Impossible to link the user position to an address')));
}

/**
 * Return the first three results of the address list
 *
 * @param {*} addresses List of addresses
 */
function getTopResults(addresses) {
  if (addresses && addresses.length) {
    return addresses.slice(0, 3).map(address => ({ saved: true, ...address }));
  }
  return [];
}

function getMatchedResults(query) {
  const lowerCaseQuery = query.toLowerCase();
  const match = address => {
    const data = getAddressLabel(address);
    return data && data.toLowerCase().indexOf(lowerCaseQuery) !== -1;
  }
  return (addresses) => {
    if (addresses && addresses.length) {
      const matchedAddresses = _.filter(addresses, match);
      return matchedAddresses.map(address => ({ saved: true, ...address }));
    }
    return [];
  };
}

export async function searchAddressesInStorage(storageKey, query) {
  const addresses = await StorageService.getItem(storageKey);
  const matchedAddresses = getMatchedResults(query)(addresses);
  return getTopResults(matchedAddresses);
}

/**
 * Add the saved addresses at the beginning of the address search results
 *
 * @param {*} localResults List of recent addresses matching the query
 * @param {*} apiResults List of addresses from geocoder api
 */
export function combineSearchResults(localResults, apiResults) {
  // If no local results then return the api results
  if (!localResults || !localResults.length) {
    return apiResults || [];
  }

  // If no search results then return the list of saved addresses
  if (!apiResults || !apiResults.length) {
    return localResults;
  }

  // Removing the selected saved addresses from the search results
  const displayedSavedAddress = localResults.map(savedAddress => savedAddress.address);
  const filteredSearchResults = apiResults.reduce((result, searchResult) => {
    if (displayedSavedAddress.indexOf(searchResult.address) === -1) {
      return result.concat(searchResult);
    }
    return result;
  }, []);

  // Returning the saved addresses first and the search results after
  return localResults.concat(filteredSearchResults);
}

export function saveRecentSearch(storageKey, address) {
  return StorageService.getItem(storageKey).then((previousList) => {
    const newList = previousList || [];
    const index = _.findIndex(newList, { address: address.address });
    if (index !== -1) { // move the address to the first head of the list when it is included already.
      newList.splice(index, 1);
    }
    // add the address to the recent list when it is not included currently.
    const length = newList.unshift({ ...address });
    if (length > MAX_RECENT_STORAGE_SIZE) {
      newList.pop();
    }
    return newList;
  }).then(addressList => StorageService.setItem(storageKey, addressList));
}

export function getAddressOfCurrentLocation() {
  return getCurrentPosition().then((position) => {
    const { latitude, longitude } = position.coords;
    return getAddressFromPosition({ latitude, longitude }).then(address => ({
      ...address,
      currentLocation: true,
    }));
  });
}

export function isCurrentLocation(address) {
  return address.category === ADDRESS_CATEGORIES.GPS;
}

export function isFindOnMap(address) {
  return address.category === ADDRESS_CATEGORIES.MAPSTOP;
}

export function getSearchIconName(address) {
  if (address.saved) return 'History';

  switch (address.type) {
    case ADDRESS_CATEGORIES.ADDRESS:
      return UNTYPED_CATEGORY_ICON.ADDRESS;
    case ADDRESS_CATEGORIES.GTFS:
      return POI_CATEGORY_ICON.PUBLIC_TRANSPORT;
    case ADDRESS_CATEGORIES.POI:
      return POI_CATEGORY_ICON[address.category] ?? POI_CATEGORY_ICON.OTHER;
    default:
      return UNTYPED_CATEGORY_ICON[address.category] ?? null;
  }
}

export function createSelectionsFromAddresses(results, query, options) {
  const topSuggestions = {
    results: [],
  };
  if (options && options.hasCurrentLocation) {
    topSuggestions.results.push(TOP_SUGGESTIONS.CURRENT_LOCATION);
  }
  if (options && options.hasFindOnMap) {
    topSuggestions.results.push(TOP_SUGGESTIONS.FIND_ON_MAP);
  }

  if (typeof query !== 'string' || _.isEmpty(query)) {
    if (_.isEmpty(results)) {
      return [topSuggestions];
    }
    return [topSuggestions, { results, title: recentSearchSectionTitle }];
  }
  return [{ results }];
}
